import React, { FormEvent, useEffect, useId, useState, useRef } from 'react';
import { useLocation } from 'react-use';

import ChevronDownIcon from 'assets/icons/chevron-down.svg';
import Location from 'assets/icons/location.svg';
import UserIcon from 'assets/icons/user.svg';

import { Button } from 'components/static/Button';

import { ERoute } from 'types/enums/ERoute';
import { IUser } from 'types/interfaces/sulu/IUser';

import GoogleMap from 'utils/GoogleMap';
import { ILocation } from 'utils/calculateDistance';
import navigateTo from 'utils/navigateTo';
import searchRadius from 'utils/searchRadius';
import getConsultant from 'utils/sulu/getConsultant';
import getConsultantList from 'utils/sulu/getConsultantList';
import getGeodata, { GeodataAPIError } from 'utils/sulu/getGeodata';
import getUserLocation from 'utils/sulu/getUserLocation';

import Dialog from '../../static/Dialog';
import LoadingSpinner from '../../static/LoadingSpinner';

interface Props {
  setFilterId: (ids: number[]) => void;
  setSearchTriggered: (triggered: boolean) => void;
  hideAdvancedSearch: boolean;
}

const saveToSessionStorage = (key: string, value: any) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

const getFromSessionStorage = (key: string, defaultValue: any) => {
  const stored = sessionStorage.getItem(key);
  return stored ? JSON.parse(stored) : defaultValue;
};

export default ({
  setFilterId,
  setSearchTriggered,
  hideAdvancedSearch,
}: Props) => {
  // Initialization of state with sessionStorage values
  const [consultantsList, setConsultantsList] = useState<IUser[] | null>(null);
  const [category, setCategory] = useState(
    getFromSessionStorage('category', null),
  );
  const [address, setAddress] = useState(
    getFromSessionStorage('address', null),
  );

  const [mapCenter, setMapCenter] = useState<ILocation>();
  const [radius, setRadius] = useState<number>(
    getFromSessionStorage('radius', 20000),
  );
  const [companyOrName, setCompanyOrName] = useState('');
  const [languages, setLanguages] = useState('');
  const [focalPoints, setFocalPoints] = useState('');
  const [country, setCountry] = useState('');
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [hasArticle, setHasArticle] = useState(false);
  const [barrierFree, setBarrierFree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState<string[]>([]);
  const [openGoogleMaps, setOpenMapIfram] = useState(false);
  const [hideFromHomepage, setHideFromHomepage] = useState(false);
  const [consultantsFound, setConsultantsFound] = useState<number[]>([]);
  const [optionsCategories, setOptionsCategories] = useState<string[]>([]);
  const [optionsLanguages, setOptionsLanguages] = useState<string[]>([]);
  const [optionsFocalPoints, setOptionsFocalPoints] = useState<string[]>([]);
  const [optionsCountries, setOptionsCountries] = useState<string[]>([]);
  const [showGeodataError, setShowGeodataError] = useState(false);
  const [geodataErrorMessage, setGeodataErrorMessage] = useState('');
  const [disableRadiusSearch, setDisableRadiusSearch] = useState(false);

  const categorySelectId = useId();
  const cityInputId = useId();
  const radiusSelectId = useId();
  const companyOrNameId = useId();
  const languagesId = useId();
  const focalPointsId = useId();
  const countryId = useId();
  const hasArticleId = useId();
  const barrierFreeId = useId();

  const defaultCenter: google.maps.LatLngLiteral = {
    lat: 54.0885,
    lng: 10.1334,
  };

  // Ref to track if the initial search has been triggered
  const initialSearchTriggered = useRef(false);

  useEffect(() => {
    setLoading(true);
    getConsultantList()
      .then(consultants => {
        if (consultants) {
          const ids = consultants.map(consultant => consultant.id);
          Promise.all(ids.map(id => getConsultant(id))).then(profiles => {
            setConsultantsList(profiles as IUser[]);
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (!address && !mapCenter) {
      getUserLocation().then(userLocation => {
        if (userLocation) {
          setMapCenter(userLocation);
        } else {
          setMapCenter(defaultCenter);
        }
      });
    }
  }, [address, mapCenter]);

  useEffect(() => {
    saveToSessionStorage('category', category);
    saveToSessionStorage('address', address);
    saveToSessionStorage('radius', radius);
  }, [category, address, radius]);

  const handleMapCenter = async () => {
    if (address) {
      try {
        const center = await getGeodata(address);
        setMapCenter(center);
        setShowGeodataError(false);
        setGeodataErrorMessage('');
        setDisableRadiusSearch(false);
      } catch (e) {
        setShowGeodataError(true);
        setMapCenter(defaultCenter);
        setDisableRadiusSearch(true);

        if (e instanceof GeodataAPIError) {
          setGeodataErrorMessage(e.message);
        } else {
          setGeodataErrorMessage(
            'Ein unbekannter Fehler bei der Standortbestimmung ist aufgetreten.',
          );
        }
      }
    } else {
      try {
        const userLocation = await getUserLocation();
        if (userLocation) {
          setMapCenter(userLocation);
          setShowGeodataError(false);
          setGeodataErrorMessage('');
          setDisableRadiusSearch(false);
        } else {
          setMapCenter(defaultCenter);
          setShowGeodataError(true);
          setGeodataErrorMessage(
            'Ihr Gerätestandort konnte nicht ermittelt werden. Bitte geben Sie manuell einen Ort oder eine PLZ ein.',
          );
          setDisableRadiusSearch(true);
        }
      } catch (e) {
        setMapCenter(defaultCenter);
        setShowGeodataError(true);
        setGeodataErrorMessage(
          'Ein Problem beim Abrufen Ihres Gerätestandorts ist aufgetreten. Bitte geben Sie manuell einen Ort oder eine PLZ ein.',
        );
        setDisableRadiusSearch(true);
      }
    }
  };

  useEffect(() => {
    if (location.pathname?.toLowerCase().includes('uebersicht'.toLowerCase())) {
      setHideFromHomepage(true);
    } else {
      setHideFromHomepage(false);
    }
  }, [location]);

  const advancedSearchHandler = () => {
    if (!hideAdvancedSearch) {
      setAdvancedSearch(!advancedSearch);
    }
  };

  const filterConsultantsByRadius = async (
    consultant: IUser,
  ): Promise<boolean> => {
    const consultantAddress = {
      lat: Number(consultant?.consultantDetails?.company?.lat),
      lng: Number(consultant?.consultantDetails?.company?.lon),
    };

    if (
      Number.isNaN(consultantAddress.lat) ||
      Number.isNaN(consultantAddress.lng)
    ) {
      return false;
    }

    if (!mapCenter) {
      return false;
    }

    const distance = await searchRadius(mapCenter, consultantAddress);
    return distance <= radius;
  };

  const setMapsPoi = () => {
    if (consultantsFound.length > 0) {
      const geoLocations =
        consultantsList
          ?.filter(
            consultant =>
              !!consultant?.consultantDetails?.company &&
              !Number.isNaN(
                Number(consultant?.consultantDetails?.company?.lat),
              ) &&
              !Number.isNaN(
                Number(consultant?.consultantDetails?.company?.lon),
              ) &&
              consultantsFound.includes(consultant.id),
          )
          .map(consultant => {
            const { firstName, lastName, consultantDetails, id } = consultant;
            const { company } = consultantDetails || {};
            const { street, plz, ort } = company || {};
            return `${company?.lat}, ${company?.lon}, ${firstName} ${lastName}, ${street}, ${plz} ${ort}, ${consultantDetails?.focalPoints}, Id=${id}`;
          }) ?? [];
      setAddresses(geoLocations);
    }
  };

  const isSearchDisabled =
    !consultantsList || consultantsList.length === 0 || loading;

  useEffect(() => {
    setMapsPoi();
  }, [consultantsFound]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    if (!hideFromHomepage) {
      navigateTo(`${ERoute.CONSULTANT_OVERVIEW}`);
    }
    setLoading(true);
    setAddresses([]);
    setShowGeodataError(false);
    setGeodataErrorMessage('');
    setDisableRadiusSearch(false);

    try {
      await handleMapCenter();

      if (!consultantsList) {
        return;
      }

      const consultantsWithinRadius = await Promise.all(
        consultantsList.map(async consultant => {
          // Only filter by radius if mapCenter is available and radius search is not disabled
          if (mapCenter && !disableRadiusSearch) {
            try {
              const isWithinRadius = await filterConsultantsByRadius(
                consultant,
              );
              return isWithinRadius ? consultant : null;
            } catch (error) {
              console.error(
                'Error filtering consultant by radius:',
                consultant.id,
                error,
              );
              return null;
            }
          }
          return consultant;
        }),
      );

      const filteredConsultants = consultantsWithinRadius
        .filter(consultant => consultant !== null)
        .filter(consultant => {
          if (!category) return true;

          return consultant?.consultantDetails?.categories?.some(c =>
            c.toLowerCase().includes(category.toLowerCase()),
          );
        })
        .filter(
          consultant =>
            !companyOrName ||
            consultant?.consultantDetails?.company?.companyName
              ?.toLowerCase()
              .includes(companyOrName.toLowerCase()) ||
            consultant?.firstName
              ?.toLowerCase()
              .includes(companyOrName.toLowerCase()) ||
            consultant?.lastName
              ?.toLowerCase()
              .includes(companyOrName.toLowerCase()),
        )
        .filter(
          consultant =>
            !languages ||
            consultant?.consultantDetails?.languages?.includes(languages),
        )
        .filter(
          consultant =>
            !focalPoints ||
            consultant?.consultantDetails?.focalPoints?.some(
              fp =>
                fp?.replace('_', ' - ')?.toLowerCase() ===
                focalPoints.toLowerCase(),
            ),
        )
        .filter(
          consultant =>
            !country ||
            consultant?.consultantDetails?.countries?.some(
              c => c.toLowerCase() === country.toLowerCase(),
            ),
        )
        .filter(
          consultant =>
            !hasArticle ||
            (!consultant?.articles || consultant?.articles.length >= 0) ===
              hasArticle,
        )
        .filter(
          consultant =>
            !barrierFree ||
            consultant?.consultantDetails?.company?.barrierFree === barrierFree,
        );

      const filteredIds = filteredConsultants.map(
        consultant => consultant?.id || 0,
      );

      setConsultantsFound(filteredIds);
      setFilterId(filteredIds);
      setSearchTriggered(true);
    } catch (error) {
      setConsultantsFound([]);
      setFilterId([]);
      setSearchTriggered(true);
    } finally {
      setLoading(false);
    }
  };

  // initial search on page load
  useEffect(() => {
    if (
      hideFromHomepage &&
      consultantsList &&
      (category || address || radius !== 20000) &&
      !initialSearchTriggered.current
    ) {
      // Create a plain Event and then cast it to unknown before casting to FormEvent
      handleSubmit(
        new Event('submit') as unknown as FormEvent<HTMLFormElement>,
      ).then();
      initialSearchTriggered.current = true; // Mark as triggered
    }
  }, [hideFromHomepage, consultantsList, category, address, radius]);

  useEffect(() => {
    const optionCategories =
      consultantsList
        ?.filter(consultant => !!consultant?.consultantDetails?.categories)
        .flatMap(consultant => {
          const { consultantDetails } = consultant;
          return consultantDetails?.categories || [];
        })
        .map(a => a.trim())
        .filter(optionCategory => optionCategory.length > 1)
        .filter(
          (optionCategory, index, self) =>
            self.indexOf(optionCategory) === index,
        )
        .sort((a, b) => a.localeCompare(b)) ?? [];
    setOptionsCategories([...new Set(optionCategories)]);
  }, [consultantsList]);

  const getUniqueOptions = (
    list: any[],
    key: 'languages' | 'focalPoints' | 'countries',
  ): string[] => {
    const processedList = (list || []).flatMap(
      consultant => consultant?.consultantDetails?.[key] || [],
    );

    const uniqueOptions = Array.from(
      new Set(
        processedList.map(item => {
          if (typeof item === 'string') {
            return item.trim().toLowerCase();
          }
          return '';
        }),
      ),
    );

    return uniqueOptions
      .filter(option => option.length > 1)
      .map(
        option =>
          processedList.find(
            original => original.trim().toLowerCase() === option,
          ) || '',
      );
  };

  useEffect(() => {
    if (!consultantsList) {
      return;
    }

    const optionLanguages = getUniqueOptions(consultantsList, 'languages').sort(
      (a, b) => a.localeCompare(b),
    );
    setOptionsLanguages(optionLanguages);

    const optionFocalPoints = getUniqueOptions(
      consultantsList,
      'focalPoints',
    ).sort((a, b) => a.localeCompare(b));
    setOptionsFocalPoints(optionFocalPoints);

    const optionCountries = getUniqueOptions(consultantsList, 'countries').sort(
      (a, b) => a.localeCompare(b),
    );
    setOptionsCountries(optionCountries);
  }, [advancedSearch, consultantsList]);

  const getLabel = () => {
    if (loading) {
      return 'Suche läuft...';
    }
    if (!consultantsList || consultantsList.length === 0) {
      return 'Suche wird geladen';
    }
    return 'Suchen';
  };

  const handleCloseGeodataErrorDialog = () => {
    setShowGeodataError(false);
    setGeodataErrorMessage('');
    setDisableRadiusSearch(true);
    setAddress('');
    setRadius(2000);
  };

  return (
    <div className='w-full flex-warp gap-4 lg:gap-6'>
      <form onSubmit={handleSubmit}>
        {!consultantsList || (consultantsList.length === 0 && loading) ? (
          <div className='loading-spinner-container'>
            <LoadingSpinner />
          </div>
        ) : (
          <div className='2xl:grid 2xl:grid-cols-6 2xl:w-3/4 2xl:justify-between'>
            <div className='xl:h-12 xl:col-span-5 xl:border form grid xl:grid-cols-9 md:grid-cols-2 grid-cols-1 gap-2 xl:rounded-full rounded-xl xl:bg-white'>
              <label
                htmlFor={categorySelectId}
                className='relative xl:col-span-3 xl:ml-2 items-center'
              >
                <UserIcon className='absolute top-4 left-4 w-4 h-4' />
                <select
                  id={categorySelectId}
                  className='xl:border-none border rounded-xl !mt-1 !pl-9 bg-white'
                  placeholder={
                    isSearchDisabled ? 'deaktiviert' : 'Wen suchen Sie?'
                  }
                  aria-label={
                    isSearchDisabled ? 'deaktiviert' : 'Wen suchen Sie?'
                  }
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  disabled={isSearchDisabled}
                >
                  <option value=''>
                    {isSearchDisabled ? 'deaktiviert' : 'Wen suchen Sie?'}
                  </option>
                  {optionsCategories.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className='absolute top-5 right-4' />
              </label>
              <label
                htmlFor={cityInputId}
                className='relative xl:col-span-2 xl:border-l-2'
              >
                <Location className='absolute top-3 left-4' />
                <input
                  id={cityInputId}
                  placeholder={
                    disableRadiusSearch || isSearchDisabled
                      ? 'deaktiviert'
                      : 'Ort oder PLZ'
                  }
                  aria-label={
                    disableRadiusSearch || isSearchDisabled
                      ? 'deaktiviert'
                      : 'Ort oder PLZ'
                  }
                  className='xl:border-none border rounded-xl !mt-1 !pl-9 bg-white'
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  disabled={isSearchDisabled || disableRadiusSearch}
                />
              </label>
              <label
                htmlFor={radiusSelectId}
                className='relative xl:col-span-2 xl:border-l-2'
              >
                <select
                  id={radiusSelectId}
                  aria-label={
                    disableRadiusSearch || isSearchDisabled
                      ? 'deaktiviert'
                      : 'Umkreissuche'
                  }
                  className='xl:border-none border rounded-xl !mt-1 bg-white'
                  value={radius}
                  onChange={e => setRadius(Number(e.target.value))}
                  disabled={isSearchDisabled || disableRadiusSearch}
                >
                  <option value='2000'>
                    {disableRadiusSearch || isSearchDisabled
                      ? 'deaktiviert'
                      : 'Umkreissuche'}
                  </option>
                  <option value='2'>Ganzer Ort</option>
                  <option value='5'>5 km</option>
                  <option value='10'>10 km</option>
                  <option value='25'>25 km</option>
                  <option value='50'>50 km</option>
                  <option value='100'>100 km</option>
                  <option value='200'>200 km</option>
                  <option value='500'>500 km</option>
                </select>
                <ChevronDownIcon className='absolute top-5 right-4' />
              </label>
              <Button
                type='submit'
                label={getLabel()}
                addClass='xl:h-[46px] xl:mt-0 xl:rounded-l-none xl:rounded-full rounded-xl flex-1 mt-1 xl:col-span-2'
                disabled={isSearchDisabled}
              />
            </div>
            {hideFromHomepage && (
              <button
                type='button'
                onClick={advancedSearchHandler}
                className='text-orange underline cursor-pointer ml-2 xl:mt-0 mt-2'
                disabled={isSearchDisabled}
              >
                Erweitere Suche {'>'}
              </button>
            )}
          </div>
        )}

        {!hideAdvancedSearch && advancedSearch && (
          <div>
            <div className='gap-4 mt-6 grid grid-cols-1 md:grid-cols-2 lg:gap-6 xl:w-3/4'>
              <label htmlFor={companyOrNameId}>
                <div>
                  <input
                    id={companyOrNameId}
                    placeholder='Unternehmen/Name'
                    aria-label='Unternehmen/Name'
                    className='pl-2 p-2 border rounded-xl w-full bg-white font-sans'
                    value={companyOrName}
                    onChange={e => setCompanyOrName(e.target.value)}
                  />
                </div>
              </label>
              <label htmlFor={languagesId}>
                <div className='relative bg-white font-sans'>
                  <select
                    id={languagesId}
                    className='py-2 p-2 border rounded-xl w-full appearance-none bg-white'
                    placeholder='Sprachen'
                    aria-label='Sprachen'
                    value={languages}
                    onChange={e => setLanguages(e.target.value)}
                  >
                    <option value=''>Sprachen</option>
                    {optionsLanguages.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className='absolute top-1/2 right-4 -translate-y-1/2' />
                </div>
              </label>

              <label htmlFor={focalPointsId}>
                <div className='relative bg-white font-sans'>
                  <select
                    id={focalPointsId}
                    className='pl-2 p-2 border rounded-xl w-full appearance-none bg-white'
                    placeholder='Schwerpunkte'
                    aria-label='Schwerpunkte'
                    value={focalPoints.replace('_', ' - ')}
                    onChange={e => setFocalPoints(e.target.value)}
                  >
                    <option value=''>Schwerpunkte</option>

                    {optionsFocalPoints.map(option => {
                      const prefix = option.split('_')[0];

                      const isHiglighted =
                        category &&
                        prefix.toLowerCase().includes(category.toLowerCase());

                      return (
                        <option
                          key={option}
                          value={option.replace('_', ' - ')}
                          className={isHiglighted ? 'font-semibold' : ''}
                        >
                          {option.replace('_', ' - ')}
                        </option>
                      );
                    })}
                  </select>
                  <ChevronDownIcon className='absolute top-1/2 right-4 -translate-y-1/2' />
                </div>
              </label>

              <label htmlFor={countryId}>
                <div className='relative bg-white font-sans'>
                  <select
                    id={countryId}
                    className='pl-2 p-2 border rounded-xl w-full appearance-none bg-white'
                    placeholder='Tätigkeitsländer'
                    aria-label='Tätigkeitsländer'
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                  >
                    <option value=''>Tätigkeitsländer</option>
                    {optionsCountries.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className='absolute top-1/2 right-4 -translate-y-1/2' />
                </div>
              </label>

              <label htmlFor={hasArticleId}>
                <div className='flex items-center mb-2'>
                  <input
                    id={hasArticleId}
                    type='checkbox'
                    aria-label='Artikel vorhanden'
                    checked={hasArticle}
                    onChange={() => setHasArticle(!hasArticle)}
                    className='mr-2 grow-0 shrink-0 font-sans'
                  />
                  <span>Artikel vorhanden</span>
                </div>
              </label>

              <label htmlFor={barrierFreeId}>
                <div className='flex items-center mb-2'>
                  <input
                    id={barrierFreeId}
                    type='checkbox'
                    aria-label='Barrierefrei'
                    checked={barrierFree}
                    onChange={() => setBarrierFree(!barrierFree)}
                    className='mr-2 grow-0 shrink-0 font-sans'
                  />
                  <span>Barrierefrei</span>
                </div>
              </label>
            </div>
          </div>
        )}
        {hideFromHomepage && (
          <>
            <div className='flex flex-col md:flex-row md:justify-between mb-2'>
              <div className='mt-6 md:block hidden'>
                {consultantsFound.length > 0 && (
                  <p>
                    {consultantsFound.length}{' '}
                    {consultantsFound.length === 1
                      ? 'passenden Anbieter gefunden'
                      : 'passende Anbieter gefunden'}
                  </p>
                )}
              </div>
              <div>
                {addresses && mapCenter && (
                  <button
                    type='button'
                    onClick={() => setOpenMapIfram(!openGoogleMaps)}
                    className='text-orange underline cursor-pointer mt-2 lg:mt-4 lg:ml-4 flex items-center'
                  >
                    <Location className='icon fill-orange text-orange mr-2' />
                    {openGoogleMaps
                      ? 'Karte schließen'
                      : 'Ergebnisse in Karte anzeigen'}
                  </button>
                )}
              </div>
              <div className='mt-6 md:hidden sm:block'>
                {consultantsFound.length > 0 && (
                  <p>
                    {consultantsFound.length}{' '}
                    {consultantsFound.length === 1
                      ? 'passenden Anbieter gefunden'
                      : 'passende Anbieter gefunden'}
                  </p>
                )}
              </div>
            </div>
            {openGoogleMaps && (
              <div>
                {addresses && mapCenter && (
                  <div className='h-[552px]'>
                    <GoogleMap
                      key='reload'
                      addresses={addresses}
                      mapCenter={mapCenter ?? defaultCenter}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </form>
      <Dialog
        isOpen={showGeodataError}
        handleToggle={handleCloseGeodataErrorDialog}
        title='Nicht verfügbar'
        headline='Umkreissuche'
        dialogPanelClassName='lg:min-w-[800px] min-w-[80%]'
        titleClassName='md:text-5xl text-3xl'
        content={
          <div className='flex flex-col justify-center items-center text-center'>
            <h3 className='text-orange mt-3'>
              Die Umkreissuche ist zurzeit nicht verfügbar.
            </h3>
            <p className='mb-8 lg:px-24'>{geodataErrorMessage}</p>
            <p className='mb-8 lg:px-24'>
              Sie können die Berater Suche weiterhin ohne die Umkreissuche
              nutzen.
            </p>
            <Button
              type='button'
              onClick={handleCloseGeodataErrorDialog}
              label='Schließen'
            />
          </div>
        }
      />
    </div>
  );
};
